import React, { useEffect, useState } from 'react';
import { ConfigProvider, Modal, Tooltip } from 'antd';
import { ColumnsType } from 'antd/es/table';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faImage, faEye } from '@fortawesome/free-solid-svg-icons';
import { useTheme } from '../../../context/ThemeContext';
import Table from '../../../commonComponents/Table';
import TablePagination from '../../../commonComponents/Pagination';
import CustomInput from '../../../commonComponents/input';
import { getAntdThemeConfig } from '../../../utils/antdTheme';
import { useSoldOutOptionsQuery } from './hooks/useSoldOutOptionsQuery';
import { useMarkOptionAvailable } from './hooks/useSoldOutOptionsMutations';
import { SoldOutOption, SoldOutOptionsProps } from './types';
import { DEFAULT_PAGE_SIZE, PAGE_SIZE_OPTIONS } from './constants';
import {
  DescriptionText,
  NoImagePlaceholder,
  OptionEyeBadge,
  OptionNameCell,
  OptionNameText,
  OptionThumbnailWrap,
  PageWrapper,
  PreviewImg,
  SearchDisabledWrapper,
  SoldOutSwitch,
  SoldOutSwitchWrap,
  StatusTag,
  TableWrapper,
  ThumbnailImg,
} from './styles';

// Must match the "Sold out options" tab's id in Menu/Constant.js
const SOLD_OUT_OPTIONS_TAB_ID = 7;

const SoldOutOptions: React.FC<SoldOutOptionsProps> = ({ tabIndex }) => {
  const { isDarkMode } = useTheme();

  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE);
  const [togglingId, setTogglingId] = useState<number | string | null>(null);
  const [previewOption, setPreviewOption] = useState<SoldOutOption | null>(null);

  const isActiveTab = tabIndex === SOLD_OUT_OPTIONS_TAB_ID;
  const { data, isLoading, isFetching, refetch } = useSoldOutOptionsQuery(
    currentPage,
    pageSize,
    isActiveTab
  );
  const { mutate: markAvailable } = useMarkOptionAvailable();

  useEffect(() => {
    if (isActiveTab) refetch();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isActiveTab]);

  const options: SoldOutOption[] = data?.options ?? [];
  const total = data?.total ?? 0;

  const handlePageChange = (page: number, size: number) => {
    setCurrentPage(page);
    setPageSize(size);
  };

  const columns: ColumnsType<SoldOutOption> = [
    {
      title: 'OPTION NAME',
      key: 'option',
      render: (_, record) => (
        <OptionNameCell>
          <OptionThumbnailWrap>
            {record.image ? (
              <>
                <ThumbnailImg src={record.image} alt={record.option} />
                <OptionEyeBadge onClick={() => setPreviewOption(record)}>
                  <FontAwesomeIcon icon={faEye} fontSize={9} color="#fff" />
                </OptionEyeBadge>
              </>
            ) : (
              <NoImagePlaceholder>
                <FontAwesomeIcon icon={faImage} fontSize={18} />
              </NoImagePlaceholder>
            )}
          </OptionThumbnailWrap>
          <OptionNameText>{record.option}</OptionNameText>
        </OptionNameCell>
      ),
    },
    {
      title: 'DESCRIPTION',
      dataIndex: 'description',
      key: 'description',
      render: (desc: string) => <DescriptionText>{desc || 'No description available'}</DescriptionText>,
    },
    {
      title: 'PRICE',
      dataIndex: 'price',
      key: 'price',
      align: 'center',
      render: (price: number) => (
        <StatusTag $isDark={isDarkMode}>
          {price !== null && price !== undefined ? `$${(Number(price) || 0).toFixed(2)}` : 'Free'}
        </StatusTag>
      ),
    },
    {
      title: 'SOLD OUT',
      key: 'sold_out',
      align: 'center',
      render: (_, record) => {
        const isThis = togglingId === record.id;
        const isBusy = togglingId !== null && !isThis;
        return (
          <SoldOutSwitchWrap $busy={isBusy}>
            <SoldOutSwitch
              $soldOut={record.sold_out}
              checked={record.sold_out}
              loading={isThis}
              disabled={isBusy}
              onChange={() => {
                setTogglingId(record.id);
                markAvailable(record, { onSettled: () => setTogglingId(null) });
              }}
              checkedChildren="Sold"
              unCheckedChildren="On"
            />
          </SoldOutSwitchWrap>
        );
      },
    },
  ];

  return (
    <ConfigProvider theme={getAntdThemeConfig(isDarkMode)}>
      <PageWrapper>
        <Tooltip title="Coming soon">
          <SearchDisabledWrapper>
            <CustomInput
              placeholder="Search options..."
              value=""
              onChange={() => {}}
              allowClear
              width="300px"
              disabled
            />
          </SearchDisabledWrapper>
        </Tooltip>

        <TableWrapper>
          <Table<SoldOutOption>
            columns={columns}
            dataSource={options}
            rowKey="id"
            loading={isLoading || isFetching}
            pagination={false}
            scroll={options.length > 0 ? { x: true } : undefined}
            disableScroll={options.length === 0}
            emptyText="No sold out options found"
            headerBackgroundColor={isDarkMode ? '#1a202c' : '#F2F2F2'}
            removeVerticalBorders
            thPadding="15px 10px"
            tdPadding="10px 10px"
            firstChildBorderRadius="8px"
            border="0"
            borderBottomLeftRight
            theme={isDarkMode ? 'dark' : 'light'}
          />
        </TableWrapper>

        {total > 0 && (
          <TablePagination
            totalCount={total}
            currentPage={currentPage}
            pageSize={pageSize}
            onChange={handlePageChange}
            totalLabel="Total sold out options"
            pageSizeOptions={PAGE_SIZE_OPTIONS}
            padding="16px 12px 0px 12px"
          />
        )}

        <Modal
          open={!!previewOption}
          onCancel={() => setPreviewOption(null)}
          centered
          width="auto"
          title={previewOption?.option}
          footer={null}
          styles={{ body: { padding: 8, display: 'flex', justifyContent: 'center', alignItems: 'center' } }}
        >
          <PreviewImg src={previewOption?.image || ''} alt={previewOption?.option} />
        </Modal>
      </PageWrapper>
    </ConfigProvider>
  );
};

export default SoldOutOptions;
